import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { AUTH_ROUTE_PATHS, AUTH_SESSION } from '@the-hive-frontend/auth-session';
import { XLoginCardComponent } from '@the-hive-frontend/shared-ui';

import { LEGAL_CONFIG } from '../../core/constants/legal.constant';
import { AuthStore } from '../../core/stores/auth.store';
import { XCardSubtitleComponent } from '../../infrastructure/components';
import { ToastManager } from '../../infrastructure/managers/ui/toast.manager';
import { getErrorMessage } from '../../infrastructure/utils/error-handler.util';
import { useStatusManager } from '../../infrastructure/utils/status-manager.util';

@Component({
  selector: 'app-login',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [XLoginCardComponent, XCardSubtitleComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent {
  private readonly auth = inject(AuthStore);
  private readonly session = inject(AUTH_SESSION);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly paths = inject(AUTH_ROUTE_PATHS);
  private readonly toast = inject(ToastManager);

  private readonly status = useStatusManager();
  readonly loading = this.status.loading;

  onPrivacyPolicy(): void {
    window.open(LEGAL_CONFIG.privacyPolicyUrl, '_blank');
  }

  onTermsAndConditions(): void {
    window.open(LEGAL_CONFIG.termsAndConditionsUrl, '_blank');
  }

  async signIn(): Promise<void> {
    this.status.setLoading(true);

    try {
      await this.auth.login();

      if (this.session.status() !== 'authenticated') {
        return;
      }

      await this.router.navigateByUrl(this.resolveTarget());
    } catch (error: unknown) {
      this.status.setError(error);
      await this.toast.error(getErrorMessage(error));
    } finally {
      this.status.setLoading(false);
    }
  }

  private resolveTarget(): string {
    return this.route.snapshot.queryParamMap.get('returnUrl') ?? this.paths.home;
  }
}
